import { z } from "zod";
import {
    doScheduleAllocationsSumTo100,
    MAX_SCHEDULE_MILESTONE_OFFSET,
    MAX_SCHEDULE_MILESTONES,
    MAX_SCHEDULE_MILESTONE_NAME_LENGTH,
} from "@common/constants/domains/accounting/schedules";
import { ScheduleMilestoneTimingRule } from "@common/helpers/domains/accounting/scheduleMilestones";
import {
    allocationPercentSchema,
    scheduleMilestoneAnchorSchema,
    scheduleMilestoneOffsetUnitSchema,
} from "@common/schemas/domains/accounting/schedule.schema";
import {
    RecognitionMethod,
    ScheduleLifecycleErrorCode,
    ScheduleMilestoneAnchor,
} from "@common/types/domains/accounting/schedule";

export const SCHEDULE_MILESTONE_OFFSET_COUNT_REQUIRED_MESSAGE =
    "Enter a count for After";
export const SCHEDULE_MILESTONE_OFFSET_UNIT_REQUIRED_MESSAGE =
    "Select a unit for After";
export const SCHEDULE_MILESTONE_SPECIFIC_DATE_REQUIRED_MESSAGE =
    "Select a specific date";
export const SCHEDULE_MILESTONE_SPECIFIC_DATE_NOT_ALLOWED_MESSAGE =
    "A specific date is only allowed with the Specific date anchor";
export const SCHEDULE_MILESTONE_OFFSET_SOURCE_DATE_ONLY_MESSAGE =
    "After counts from the source date; use On with a specific date";

export interface ScheduleMilestoneTimingMessages {
    offsetCountRequired: string;
    offsetUnitRequired: string;
    offsetSourceDateOnly: string;
    specificDateRequired: string;
    specificDateNotAllowed: string;
}

const DEFAULT_SCHEDULE_MILESTONE_TIMING_MESSAGES: ScheduleMilestoneTimingMessages =
    {
        offsetCountRequired: SCHEDULE_MILESTONE_OFFSET_COUNT_REQUIRED_MESSAGE,
        offsetUnitRequired: SCHEDULE_MILESTONE_OFFSET_UNIT_REQUIRED_MESSAGE,
        offsetSourceDateOnly:
            SCHEDULE_MILESTONE_OFFSET_SOURCE_DATE_ONLY_MESSAGE,
        specificDateRequired: SCHEDULE_MILESTONE_SPECIFIC_DATE_REQUIRED_MESSAGE,
        specificDateNotAllowed:
            SCHEDULE_MILESTONE_SPECIFIC_DATE_NOT_ALLOWED_MESSAGE,
    };

/**
 * offsetCount and offsetUnit are both null ("On") or both set ("After");
 * After counts from the source date only; specificDate iff the anchor is
 * specificDate. A form passes its own copy for the messages it shows.
 */
export function createScheduleMilestoneTimingRefine(
    messages: Partial<ScheduleMilestoneTimingMessages> = {},
): (value: ScheduleMilestoneTimingRule, ctx: z.RefinementCtx) => void {
    const copy = { ...DEFAULT_SCHEDULE_MILESTONE_TIMING_MESSAGES, ...messages };
    return (value, ctx) => {
        if (value.offsetCount === null && value.offsetUnit !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["offsetCount"],
                message: copy.offsetCountRequired,
            });
        }
        if (value.offsetCount !== null && value.offsetUnit === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["offsetUnit"],
                message: copy.offsetUnitRequired,
            });
        }
        const isSpecificDateAnchor =
            value.anchor === ScheduleMilestoneAnchor.specificDate;
        if (isSpecificDateAnchor && value.offsetCount !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["anchor"],
                message: copy.offsetSourceDateOnly,
            });
        }
        if (isSpecificDateAnchor && value.specificDate === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["specificDate"],
                message: copy.specificDateRequired,
            });
        }
        if (!isSpecificDateAnchor && value.specificDate !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["specificDate"],
                message: copy.specificDateNotAllowed,
            });
        }
    };
}

export const refineScheduleMilestoneTiming =
    createScheduleMilestoneTimingRefine();

export const scheduleTemplateMilestoneInputSchema = z
    .object({
        name: z.string().trim().min(1).max(MAX_SCHEDULE_MILESTONE_NAME_LENGTH),
        allocationPercent: allocationPercentSchema,
        anchor: scheduleMilestoneAnchorSchema,
        offsetCount: z
            .number()
            .int()
            .positive()
            .max(MAX_SCHEDULE_MILESTONE_OFFSET)
            .nullable(),
        offsetUnit: scheduleMilestoneOffsetUnitSchema.nullable(),
        specificDate: z.string().date().nullable(),
    })
    .superRefine(refineScheduleMilestoneTiming);

export type ScheduleTemplateMilestoneInput = z.infer<
    typeof scheduleTemplateMilestoneInputSchema
>;

export const scheduleTemplateMilestonesInputSchema = z
    .array(scheduleTemplateMilestoneInputSchema)
    .max(MAX_SCHEDULE_MILESTONES)
    .default([]);

export const SCHEDULE_MILESTONES_MUST_SUM_MESSAGE =
    "Milestone allocations must sum to 100%";
export const SCHEDULE_MILESTONE_COMPONENT_ALONE_MESSAGE =
    "A milestone schedule has one recognition row";
export const SCHEDULE_MILESTONES_REQUIRED_MESSAGE =
    "Add at least one milestone";
export const SCHEDULE_MILESTONES_NOT_ALLOWED_MESSAGE =
    "Milestones are only allowed on a milestone schedule";
export const SCHEDULE_MILESTONE_OVERRIDE_MESSAGE =
    "Use a milestone policy for milestone recognition";
export const SCHEDULE_MILESTONE_EXAMPLE_DATES_UNIQUE_MESSAGE =
    "Each milestone can have one example date";
export const SCHEDULE_MILESTONE_EXAMPLE_DATE_UNKNOWN_MESSAGE =
    "An example date must name one of the milestones";

export function hasMilestoneComponent(
    components: ReadonlyArray<{ method: RecognitionMethod }>,
): boolean {
    return components.some(
        (component) => component.method === RecognitionMethod.milestone,
    );
}

interface ScheduleMilestoneConfigValue {
    components: ReadonlyArray<{ method: RecognitionMethod }>;
    milestones?: ReadonlyArray<{ allocationPercent: number }>;
}

/** A milestone plan has at least one row and its rows sum to 100%. */
export function refineMilestonePlan(
    milestones: ReadonlyArray<{ allocationPercent: number }>,
    ctx: z.RefinementCtx,
): void {
    if (milestones.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["milestones"],
            message: SCHEDULE_MILESTONES_REQUIRED_MESSAGE,
        });
        return;
    }
    if (
        !doScheduleAllocationsSumTo100(
            milestones.map((milestone) => milestone.allocationPercent),
        )
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["milestones"],
            message: SCHEDULE_MILESTONES_MUST_SUM_MESSAGE,
        });
    }
}

export function refineScheduleMilestones(
    value: ScheduleMilestoneConfigValue,
    ctx: z.RefinementCtx,
): void {
    const milestones = value.milestones ?? [];
    if (!hasMilestoneComponent(value.components)) {
        if (milestones.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["milestones"],
                message: SCHEDULE_MILESTONES_NOT_ALLOWED_MESSAGE,
            });
        }
        return;
    }
    if (value.components.length !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["components"],
            message: SCHEDULE_MILESTONE_COMPONENT_ALONE_MESSAGE,
        });
    }
    refineMilestonePlan(milestones, ctx);
}

/** A preview's example day for one milestone row, replacing the resolved one. */
export const milestoneExampleDateSchema = z.object({
    sortOrder: z.number().int().positive(),
    expectedDate: z.string().date(),
});

export type MilestoneExampleDate = z.infer<typeof milestoneExampleDateSchema>;

/** Preview rows take sort orders 1..n in the order they are sent. */
export function refineMilestoneExampleDates(
    value: {
        milestones: readonly object[];
        milestoneExampleDates?: readonly MilestoneExampleDate[];
    },
    ctx: z.RefinementCtx,
): void {
    const sortOrders = (value.milestoneExampleDates ?? []).map(
        (example) => example.sortOrder,
    );
    if (new Set(sortOrders).size !== sortOrders.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["milestoneExampleDates"],
            message: SCHEDULE_MILESTONE_EXAMPLE_DATES_UNIQUE_MESSAGE,
        });
    }
    if (sortOrders.some((sortOrder) => sortOrder > value.milestones.length)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["milestoneExampleDates"],
            message: SCHEDULE_MILESTONE_EXAMPLE_DATE_UNKNOWN_MESSAGE,
        });
    }
}

export const recognizeScheduleMilestoneBodySchema = z.object({
    entityId: z.number().int().positive(),
    /** The journal entry date; defaults to the milestone's expected date. */
    postingDate: z.string().date().optional(),
});

export type RecognizeScheduleMilestoneBody = z.infer<
    typeof recognizeScheduleMilestoneBodySchema
>;

export const recognizeScheduleMilestoneResponseSchema = z.object({
    journalEntryId: z.string().uuid(),
    periodId: z.string().uuid(),
    sortOrder: z.number().int(),
    postingDate: z.string().date(),
    amount: z.number(),
    isOverRecognized: z.boolean(),
    applicationRemainingAmount: z.number(),
});

export type RecognizeScheduleMilestoneResponse = z.infer<
    typeof recognizeScheduleMilestoneResponseSchema
>;

/** Returned (409) when the milestone's recognition was already posted. */
export const scheduleMilestoneAlreadyPostedErrorSchema = z.object({
    errorCode: z.literal(ScheduleLifecycleErrorCode.alreadyPosted),
    message: z.string(),
});

export type ScheduleMilestoneAlreadyPostedError = z.infer<
    typeof scheduleMilestoneAlreadyPostedErrorSchema
>;

export const SCHEDULE_MILESTONE_RECOGNIZE_BULK_LIMIT = 200;

export const scheduleMilestoneRefSchema = z.object({
    applicationId: z.string().uuid(),
    sortOrder: z.number().int().positive(),
});

export type ScheduleMilestoneRef = z.infer<typeof scheduleMilestoneRefSchema>;

export const recognizeScheduleMilestoneBulkItemSchema =
    scheduleMilestoneRefSchema.extend({
        /** The journal entry date for this row; overrides the batch date. */
        postingDate: z.string().date().optional(),
    });

export type RecognizeScheduleMilestoneBulkItem = z.infer<
    typeof recognizeScheduleMilestoneBulkItemSchema
>;

export const recognizeScheduleMilestonesBulkBodySchema = z.object({
    entityId: z.number().int().positive(),
    /**
     * The journal entry date for every row without its own; a row with
     * neither posts on its milestone's expected date.
     */
    postingDate: z.string().date().optional(),
    milestones: z
        .array(recognizeScheduleMilestoneBulkItemSchema)
        .min(1)
        .max(SCHEDULE_MILESTONE_RECOGNIZE_BULK_LIMIT)
        .refine(
            (refs) =>
                new Set(
                    refs.map((ref) => `${ref.applicationId}:${ref.sortOrder}`),
                ).size === refs.length,
            { message: "Milestones must be unique" },
        ),
});

export type RecognizeScheduleMilestonesBulkBody = z.infer<
    typeof recognizeScheduleMilestonesBulkBodySchema
>;

export const recognizeScheduleMilestonesBulkFailureSchema =
    scheduleMilestoneRefSchema.extend({ message: z.string() });

export type RecognizeScheduleMilestonesBulkFailure = z.infer<
    typeof recognizeScheduleMilestonesBulkFailureSchema
>;

/** Per-row outcome: each milestone posts (or fails) on its own. */
export const recognizeScheduleMilestonesBulkResponseSchema = z.object({
    recognized: z.array(
        recognizeScheduleMilestoneResponseSchema.extend({
            applicationId: z.string().uuid(),
        }),
    ),
    failed: z.array(recognizeScheduleMilestonesBulkFailureSchema),
});

export type RecognizeScheduleMilestonesBulkResponse = z.infer<
    typeof recognizeScheduleMilestonesBulkResponseSchema
>;
